import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { NS as U340X_KPI_NS } from '../u340xKpiShared/u340xKpiNs';
import { U340X_KPI_DEFAULT_MONTHS } from '../u340xKpiShared/types/u340xKpiTypes';
import { u340xTodayOrgYm } from '../u340xKpiShared/utils/u340xKpiYm';
import {
  doInitLU,
  luHandleAppiError,
  U3402_LU_CODE,
  type U3402InitLU,
} from './U3402Service';
import { U3402ContextProvider } from './U3402Context';

export const NS = 'u3402';

export const U3402ROLES = {
  READ: 'U3402:READ',
} as const;
export type U3402ROLES = (typeof U3402ROLES)[keyof typeof U3402ROLES];

const defaultInitLU: U3402InitLU = {
  defaultMonth: u340xTodayOrgYm(),
  monthPreference: {
    id: null,
    version: null,
    logicalUnitCode: U3402_LU_CODE,
    monthsTile: U340X_KPI_DEFAULT_MONTHS,
    monthsStandalone: null,
    monthsStandaloneEffective: U340X_KPI_DEFAULT_MONTHS,
  },
  tile: {
    code: U3402_LU_CODE,
    labelFr: 'KPI TEC',
    labelEn: 'WIP KPI',
  },
};

const U3402: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3402InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces([NS, U340X_KPI_NS], (err) => {
      if (err) {
        console.error('Error loading namespace:', err);
        return;
      }
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3402InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3402InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        monthPreference: initLU.monthPreference ?? defaultInitLU.monthPreference,
        tile: initLU.tile ?? defaultInitLU.tile,
        defaultMonth: initLU.defaultMonth ?? defaultInitLU.defaultMonth,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(
        error as Error,
        t,
        NS,
        luHandleAppiError,
      ) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('screenTitle', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3402ContextProvider initLU={initLUData}>
      <Outlet />
    </U3402ContextProvider>
  );
};

export default U3402;
